<script setup lang="ts">
import type { SiteBlockData, SiteBlockSettings } from '../../../types/site'

/**
 * Standalone hero band — optional background image with overlay, eyebrow,
 * large headline, sub, and a CTA. Unlike the homepage hero layouts (which
 * are driven by tenant profile data), this is a self-contained block that
 * can be placed on any page.
 *
 * `data.animation` reuses the same background-animation set as the homepage
 * hero layouts (SplitHero/CenteredHero/EditorialHero), so the effect is
 * reachable on any page, not just the tenant's home. Only shown without a
 * background image — an image already covers the full section, so the
 * animation would render underneath it and never be visible.
 */
const props = defineProps<{ data: SiteBlockData, settings: SiteBlockSettings }>()

const isExternal = computed(() =>
  !!props.data.button_url && /^(https?:|mailto:|tel:)/.test(props.data.button_url))

const alignClass = computed(() =>
  props.data.align === 'left' ? 'text-left items-start' : 'text-center items-center')

const hasImage = computed(() => !!props.data.image)
</script>

<template>
  <div
    class="relative overflow-hidden rounded-[var(--site-radius)]"
    :class="hasImage ? '' : 'site-bg-accent-soft'"
  >
    <SiteAnimationsHeroAnimation
      v-if="!hasImage"
      :animation="data.animation ?? 'none'"
    />
    <img
      v-if="hasImage"
      :src="data.image"
      :alt="data.headline || ''"
      class="absolute inset-0 h-full w-full object-cover"
      loading="lazy"
    >
    <div
      v-if="hasImage && data.overlay"
      class="absolute inset-0 bg-black/50"
      aria-hidden="true"
    />

    <div
      class="relative mx-auto flex max-w-3xl flex-col gap-5 px-6 py-20 sm:py-28"
      :class="[alignClass, hasImage ? 'text-white' : '']"
    >
      <p
        v-if="data.eyebrow"
        class="text-xs font-semibold uppercase tracking-[0.2em]"
        :class="hasImage ? 'text-white/80' : 'text-[var(--site-accent)]'"
      >
        {{ data.eyebrow }}
      </p>
      <h2
        v-if="data.headline"
        class="site-display text-4xl leading-[1.08] tracking-tight sm:text-5xl"
      >
        {{ data.headline }}
      </h2>
      <p
        v-if="data.subtext"
        class="max-w-2xl text-lg leading-relaxed"
        :class="hasImage ? 'text-white/85' : 'text-[var(--site-muted)]'"
      >
        {{ data.subtext }}
      </p>
      <div v-if="data.button_text && data.button_url">
        <NuxtLink
          :to="data.button_url"
          :external="isExternal"
          class="site-btn-accent inline-block px-6 py-3 text-base font-semibold transition"
        >
          {{ data.button_text }}
        </NuxtLink>
      </div>
    </div>
  </div>
</template>
